import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import { Button } from "../../../components/Button/Button";
import { ItemTile } from "../../../components/ItemTile/ItemTile";
import { Collapse } from "../../../components/Collapse/Collapse";
import { PreviewApiClient, PreviewView, StylePreviewModal } from "./StylePreviewModal";

interface PreviewSectionProps {
  isLocked: boolean;
  isOpen: boolean;
  onToggle: () => void;
  /** Style as it would be saved right now. */
  getPreviewStyle: () => any;
  scoresheetSchemaId?: string;
  useApiClient: () => PreviewApiClient;
}

export const PreviewSection: React.FC<PreviewSectionProps> = ({
  isLocked,
  isOpen,
  onToggle,
  getPreviewStyle,
  scoresheetSchemaId,
  useApiClient,
}) => {
  const { t } = useTranslation();
  const [view, setView] = useState<PreviewView | null>(null);
  const [previewStyle, setPreviewStyle] = useState<any>(null);

  const open = (next: PreviewView) => {
    setPreviewStyle(getPreviewStyle());
    setView(next);
  };

  return (
    <Collapse
      title={
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            {isLocked && <span className="icon-[ph--lock] text-[#877369] text-base" />}
            <span
              className={classNames(
                "text-lg font-bold font-header",
                isLocked ? "text-[#877369]" : "text-[#6c2f00]",
              )}
            >
              {t("Pages:StyleCreator.Preview.Title", "7. Preview")}
            </span>
          </div>
          {!isLocked && (
            <span className="text-xs text-[#54433a] font-normal font-sans">
              {t(
                "Pages:StyleCreator.Preview.Subtitle",
                "Review how this style will look for participants and judges.",
              )}
            </span>
          )}
        </div>
      }
      isOpen={isOpen}
      onToggle={onToggle}
      variant="plain"
      density="comfortable"
      className={classNames(
        "rounded-xl border border-solid transition-all duration-200",
        isLocked ? "opacity-60 cursor-not-allowed" : "",
      )}
      headerClassName={isLocked ? "pointer-events-none" : ""}
    >
      <fieldset
        disabled={isLocked}
        className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full"
      >
        <ItemTile
          title={t(
            "Pages:StyleCreator.Preview.Participant",
            "Participant view",
          )}
          meta={
            <div className="flex items-center gap-3 mt-1 font-sans">
              <div className="w-8 h-8 rounded-full bg-[#ffdcc3] flex items-center justify-center text-[#2f1500] flex-shrink-0">
                <span className="icon-[ph--user] text-lg" />
              </div>
              <span className="text-xs text-[#54433a]">
                {t(
                  "Pages:StyleCreator.Preview.ParticipantDesc",
                  "This is what participants will see.",
                )}
              </span>
            </div>
          }
          actions={
            <Button
              htmlType="button"
              type="base"
              variant="ghost"
              onClick={() => open("participant")}
              className="bg-white border border-[#dac2b6] text-[#6c2f00] font-bold text-xs px-4 py-2 rounded-lg hover:bg-[#fff1e9]"
            >
              {t("Pages:StyleCreator.Preview.Open", "Open preview")}
            </Button>
          }
          className="bg-[#fff1e9]"
        />

        <ItemTile
          title={t("Pages:StyleCreator.Preview.Judge", "Judge view")}
          meta={
            <div className="flex items-center gap-3 mt-1 font-sans">
              <div className="w-8 h-8 rounded-full bg-[#b1f0ce] flex items-center justify-center text-[#002114] flex-shrink-0">
                <span className="icon-[ph--flask] text-lg" />
              </div>
              <span className="text-xs text-[#54433a]">
                {t(
                  "Pages:StyleCreator.Preview.JudgeDesc",
                  "This is what judges will see.",
                )}
              </span>
            </div>
          }
          actions={
            <Button
              htmlType="button"
              type="base"
              variant="ghost"
              onClick={() => open("judge")}
              className="bg-white border border-[#dac2b6] text-[#6c2f00] font-bold text-xs px-4 py-2 rounded-lg hover:bg-[#fff1e9]"
            >
              {t("Pages:StyleCreator.Preview.Open", "Open preview")}
            </Button>
          }
          className="bg-[#fff1e9]"
        />
      </fieldset>

      <StylePreviewModal
        view={view}
        onClose={() => setView(null)}
        style={previewStyle}
        scoresheetSchemaId={scoresheetSchemaId}
        useApiClient={useApiClient}
      />
    </Collapse>
  );
};
